import { Content, MediaType } from "@/types";

/**
 * Detects media type from a MIME type string (e.g., "image/png", "audio/mp3")
 * Used when uploading files where we have access to the File.type property
 */
export const getMediaTypeFromMimeType = (mimeType: string): MediaType => {
  if (mimeType.startsWith("image/")) {
    return "image";
  }
  if (mimeType.startsWith("video/")) {
    return "video";
  }
  if (mimeType.startsWith("audio/")) {
    return "audio";
  }
  if (mimeType === "application/pdf") {
    return "application/pdf";
  }
  return "image"; // fallback default
};

/**
 * Detects media type from a URL (including data URLs)
 * Used when displaying media where we only have the URL
 */
export const detectMediaTypeFromUrl = (url: string, declaredType?: string): MediaType => {
  // If already a valid type, use it
  if (
    declaredType === "image" ||
    declaredType === "audio" ||
    declaredType === "video" ||
    declaredType === "application/pdf"
  ) {
    return declaredType;
  }

  const lowerUrl = url.toLowerCase();

  // Check data URLs
  if (lowerUrl.startsWith("data:")) {
    if (lowerUrl.startsWith("data:image/")) {
      return "image";
    }
    if (lowerUrl.startsWith("data:audio/")) {
      return "audio";
    }
    if (lowerUrl.startsWith("data:video/")) {
      return "video";
    }
    if (lowerUrl.startsWith("data:application/pdf")) {
      return "application/pdf";
    }
  }

  // Check file extensions
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(lowerUrl)) {
    return "image";
  }
  if (/\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/i.test(lowerUrl)) {
    return "audio";
  }
  if (/\.(mp4|webm|mov|avi|mkv)(\?|$)/i.test(lowerUrl)) {
    return "video";
  }
  if (/\.pdf(\?|$)/i.test(lowerUrl)) {
    return "application/pdf";
  }

  // Default to image for unknown types
  return "image";
};

/**
 * Human-readable labels for media types
 */
export const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  image: "Image",
  audio: "Audio",
  video: "Video",
  "application/pdf": "PDF Document",
};

/**
 * Extracts or generates a display title for media
 * Priority: provided title > filename from URL > type-based fallback
 */
export const getMediaTitle = (url: string, title?: string, detectedType?: MediaType): string => {
  // Use provided title if it's meaningful
  if (title && title !== "media") {
    return title;
  }

  // Try to extract filename from URL (not data URLs)
  if (url && !url.startsWith("data:")) {
    const urlPath = url.split("?")[0];
    const filename = urlPath.split("/").pop();
    if (filename) {
      return filename;
    }
  }

  // Fallback to type-based label
  if (detectedType) {
    return MEDIA_TYPE_LABELS[detectedType] || "Media";
  }

  return "Media";
};

/**
 * Checks if the content array has exactly one text block
 */
export const isSingleTextBlock = (content: Content[]): boolean => {
  if (!Array.isArray(content)) {
    return false;
  }
  return content.filter(c => c.type === "text").length === 1;
};
